import React, { useState, useEffect } from "react";
const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
import { generateRecommendations  } from "../utils/RecommendationEngine";
import { generateHeartbeat } from "../utils/generateHeartbeat";
import { useDashboard } from "./dashboardStore";

function prettifyColumnName(col) {
  return col
    .replace(/_/g, " ")
    .replace(/([A-Z])/g, " $1")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

function toCamelCaseKey(key) {
  if (!key) return key;
  if (key.toLowerCase() === "createdat") return "CreatedAt";
  return key;
}

function normalizeRowKeys(row) {
  const newRow = {};
  Object.keys(row).forEach((key) => {
    const newKey = toCamelCaseKey(key);
    newRow[newKey] = row[key];
    if (key === "ChangeType") {
      newRow[newKey] = row[key] === "I/U" ? "Created/Updated" : "Deleted";
    }
  });
  return newRow;
}

const ChangesAndRecommendations = () => {
const [items, setItems] = useState([]); // Processed changes + recommendations
const [search, setSearch] = useState(""); // Search query
const [backups, setBackups] = useState([]); // Backup entries
const [retentionP, setRetentionP] = useState(7); // Retention period for backups
const [currentDayPage, setCurrentDayPage] = useState(0); // Day-level pagination
const [currentItemPage, setCurrentItemPage] = useState(0); // Item-level pagination
const [itemsPerPage, setItemsPerPage] = useState(5); // Items per page for table
	const [message, setMessage] = useState("");

// Subscribe to dashboard store slice
const changes = useDashboard(state => state.data?.changes);
const costsDataFromStore = useDashboard(state => state.data?.costs);
const poDataFromStore = useDashboard(state => state.data?.purchaseOrders);
const customersDataFromStore = useDashboard(state => state.data?.customers);
const suppliersDataFromStore = useDashboard(state => state.data?.suppliers);
const invoicesDataFromStore = useDashboard(state => state.data?.invoices);

const dataFromStore = useDashboard(state => state.data);


	// Auto-hide success message after 3s
	useEffect(() => {
		if (!message) return;
		const timer = setTimeout(() => setMessage(""), 4000);
		return () => clearTimeout(timer);
	}, [message]);


// -------------------------------------
// Process changes and recommendations
// -------------------------------------
useEffect(() => {
	// Wait until store has data
	if (!changes) return;

	const processItems = async () => {
		try {
			// Map and normalize each row
			let parsedItems = changes.map((row) => {
				let rowData = {};
				try {
					const excludeKeys = ["created_at", "total", "LastModified", "date", "RecordedAt"];
					rowData = normalizeRowKeys(
						Object.fromEntries(
							Object.entries(row).filter(([key]) => !excludeKeys.includes(key))
						)
					);

					// Format date fields
					["CreatedAt", "LastModified", "DeletedAt"].forEach((field) => {
						if (rowData[field]) {
							const d = new Date(rowData[field]);
							rowData[field] = d.toLocaleString("en-GB", {
								day: "2-digit",
								month: "short",
								year: "numeric",
							});
						}
					});
				} catch (err) {
					console.warn("Failed to parse RowData:", row.RowData);
				}
				return { ...rowData, recommendations: "" };
			});

			// Sort by CreatedAt descending
			parsedItems.sort(
				(a, b) => new Date(b.CreatedAt).getTime() - new Date(a.CreatedAt).getTime()
			);

			// Generate recommendations
              const recs = generateRecommendations(parsedItems, {
                costsDat: costsDataFromStore,
                poDat: poDataFromStore,
                customersDat: customersDataFromStore,
                suppliersDat: suppliersDataFromStore,
                invoicesDat: invoicesDataFromStore,
              });
			const itemsWithRecs = parsedItems.map((item) => {
				const rec = recs.find((r) => r.Id === item.Id);
				return { ...item, recommendations: rec ? rec.message : "" };
			});

			setItems(itemsWithRecs);
		} catch (err) {
			console.error("Error processing items or recommendations:", err);
		}
	};

	processItems();
}, [changes, costsDataFromStore, poDataFromStore, customersDataFromStore, suppliersDataFromStore, invoicesDataFromStore]);

// -------------------------------------
// Fetch backups (independent of dashboard store) - strict mode + super lazy
// Fetches 15 seconds after dashboard skeleton to allow critical widgets to load
// Partially sync model, then cached for 24 hours
// -------------------------------------
const [loadingBackups, setLoadingBackups] = useState(false);
useEffect(() => {
	let didRun = false; // per mount

	// Try to read from cache
	const cached = JSON.parse(localStorage.getItem("backupsCache") || "null");
	const now = Date.now();
	if (cached && now - cached.timestamp < 24 * 60 * 60 * 1000) {
		setBackups(cached.data);
		setRetentionP(cached.data[0]?.retentionDays || 7);
		setLoadingBackups(false);
		// no need to delay fetch if cache exists, but still refresh in background
		return;
		
	}

	setLoadingBackups(true);

	const timer = setTimeout(async () => {
		if (didRun) return;
		didRun = true;

		try {
			const res = await fetch(`${apiBaseUrl}/api/backups`);
			if (!res.ok) throw new Error("Failed to fetch backups");
			const data = await res.json();

			if (data[0]?.retentionDays) setRetentionP(data[0].retentionDays);

			const expandedBackups = data.flatMap((db) => {
				const days = 3;
				return Array.from({ length: days }, (_, i) => {
					const date = new Date();
					date.setDate(date.getDate() - i);
					return {
						id: `${db.database}-backup-${i + 1}`,
						name: `${db.database} Backup ${i + 1}`,
						lastBackupTaken: date.toISOString(),
					};
				});
			});

			setBackups(expandedBackups);

			// Save to cache
			localStorage.setItem(
				"backupsCache",
				JSON.stringify({ data: expandedBackups, timestamp: Date.now() })
			);
		} catch (err) {
			console.error("Error fetching backups:", err);
		} finally {
			setLoadingBackups(false);
		}
	}, 10000); // lazy 10s fetch

	return () => clearTimeout(timer); // cleanup
}, []);


  const tableColumns = items.length > 0 ? Object.keys(items[0]) : [];

  const filteredItems = items.filter((item) =>
    tableColumns.some((col) => String(item[col]).toLowerCase().includes(search.toLowerCase()))
  );

  // --- DATE + ITEM LEVEL PAGINATION ---
  const daysToShow = 7;
  const today = new Date();
  const itemsByDate = {};
  for (let i = 0; i < daysToShow; i++) {
    const date = new Date(today);
    date.setDate(today.getDate() - i);
    itemsByDate[date.toDateString()] = [];
  }

  filteredItems.forEach((item) => {
    const dateStr = item.DeletedAt || item.CreatedAt;
    const date = new Date(dateStr).toDateString();
    if (!itemsByDate[date]) itemsByDate[date] = [];
    itemsByDate[date].push(item);
  });

  const days = Object.keys(itemsByDate).sort((a, b) => new Date(b) - new Date(a));

  const pagedItemsByDay = days.map((day) => {
    const dayItems = itemsByDate[day];
    const chunks = [];
    for (let i = 0; i < dayItems.length; i += itemsPerPage) {
      chunks.push(dayItems.slice(i, i + itemsPerPage));
    }
    return chunks;
  });

  const paginatedItems = pagedItemsByDay[currentDayPage]?.[currentItemPage] || [];
  const totalDayPages = days.length;
  const totalItemPages = pagedItemsByDay[currentDayPage]?.length || 1;

  // --- END PAGINATION ---
  return (
    <div className="w-full max-w-8xl mx-auto p-4">
      {/* Search */}
{/* Search + Items per day */}
<div className="flex flex-col mb-4">


<div className="w-full flex justify-center mb-10">
  <button
    disabled={!dataFromStore || Object.keys(dataFromStore).length === 0}

    onClick={() => {
      if (
        confirm(
          `Do you want to generate a heartbeat?\n\n(This may slow down performance for other users for 1-2 seconds)`
        )
      ) {
      if (!dataFromStore) {
          alert("Try again in 1 minute, the data is still loading");
          return;
      }
        generateHeartbeat(dataFromStore);
        setMessage(`Generating Heartbeat... Wait atleast 3-5 seconds.`);
      }
    }}
    className="flex cursor-pointer flex-col items-center justify-center gap-2 px-6 py-4 
               bg-red-200 text-red-800 rounded-xl shadow-md hover:bg-red-300 
               active:scale-95 transition transform font-semibold"
  >
    <span className="text-3xl">❤️</span>
    <span className="text-lg">Generate Heartbeat</span>
  </button>
</div>




  <div className="flex flex-wrap items-center gap-4 justify-center">
    {/* Search */}
    <input
      type="text"
      placeholder="Search..."
      className="px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring focus:ring-blue-200 flex-1 min-w-[200px]"
      value={search}
      onChange={(e) => setSearch(e.target.value)}
    />

    {/* Items per day selector */}
    <div className="flex items-center gap-2">
      <label className="text-gray-700 font-medium">Items per day:</label>
      <select
        className="px-2 py-1 border rounded-md"
        value={itemsPerPage}
        onChange={(e) => {
          setItemsPerPage(parseInt(e.target.value));
          setCurrentItemPage(0); // reset item page
        }}
      >
        {[5, 20, 50, 100].map((n) => (
          <option key={n} value={n}>{n}</option>
        ))}
      </select>
    </div>
  </div>

  {/* Day title */}
  {days.length > 0 && (
    <div className="mt-2 text-black-700 font-medium text-center text-lg">
      Changes on{" "}
      {new Date(days[currentDayPage]).toLocaleDateString("en-GB", {
        weekday: "long",
        day: "2-digit",
        month: "short",
        year: "numeric",
      })}
    </div>
  )}
</div>


      {/* Changes Table */}
      <div className="overflow-x-auto rounded-md border border-gray-200 mb-6">
        <table className="min-w-full divide-y divide-gray-100 text-sm md:text-base">
          <thead className="bg-gray-50">
            <tr>
              {tableColumns.map((col) => (
                <th key={col} className="px-3 py-4 text-center font-medium text-gray-700">
                  {prettifyColumnName(col)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {paginatedItems.length === 0 ? (
              <tr>
                <td colSpan={tableColumns.length + 1} className="px-3 py-4 text-center text-blue-500 text-lg italic">
                  Nothing was done on this day.
                </td>
              </tr>
            ) : (
              paginatedItems.map((item, idx) => (
                <tr key={idx} className="hover:bg-gray-300 even:bg-gray-200 odd:bg-white">
                  {tableColumns.map((col) => {
                    let textClass = "text-gray-700";
                    if (col === "ChangeType") {
                      if (item[col] === "Created/Updated") textClass = "text-green-600";
                      else if (item[col] === "Deleted") textClass = "text-red-600";
                    }
                    return (
                      <td key={col} className={`px-3 py-2 text-center ${textClass}`}>
                        {item[col] ?? "N/A"}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

{/* Pagination */}
<div className="flex flex-col items-center gap-5 mb-10">
  {/* Item navigation */}
  {totalItemPages > 1 && (
    <div className="flex gap-2">
      <button
        className="px-3 py-1 border cursor-pointer rounded-md hover:bg-gray-100"
        disabled={currentItemPage === 0}
        onClick={() => setCurrentItemPage(p => p - 1)}
      >
        Previous Items
      </button>
      <span className="px-3 py-1 text-gray-700">{currentItemPage + 1} / {totalItemPages}</span>
      <button
        className="px-3 py-1 cursor-pointer border rounded-md hover:bg-gray-100"
        disabled={currentItemPage === totalItemPages - 1}
        onClick={() => setCurrentItemPage(p => p + 1)}
      >
        Next Items
      </button>
    </div>
  )}

  {/* Day navigation */}
  <div className="flex gap-2">
    <button
      className="px-3 py-1 border cursor-pointer rounded-md hover:bg-gray-100"
      disabled={currentDayPage === 0}
      onClick={() => { setCurrentDayPage(p => p - 1); setCurrentItemPage(0); }}
    >
      Previous Day
    </button>
    <span className="px-3 py-1 text-gray-700">{currentDayPage + 1} / {totalDayPages}</span>
    <button
      className="px-3 py-1 cursor-pointer border rounded-md hover:bg-gray-100"
      disabled={currentDayPage === totalDayPages - 1}
      onClick={() => { setCurrentDayPage(p => p + 1); setCurrentItemPage(0); }}
    >
      Next Day
    </button>
  </div>
</div>

          <div className="mt-2 text-black-700 font-medium text-center text-lg">
            Data Backups (Hosted On Microsoft Servers)
            </div>
            
      {/* Backups Table */}
      <div className="overflow-x-auto rounded-md border border-gray-200">
        <table className="min-w-full divide-y divide-gray-100 text-sm md:text-base">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-4 text-center font-medium text-gray-700">#</th>
              <th className="px-3 py-4 text-center font-medium text-gray-700">Backup Name</th>
              <th className="px-3 py-4 text-center font-medium text-gray-700">Timestamp</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {backups.length === 0 ? (
              <tr>
                <td colSpan={3} className="px-3 py-4 text-center text-gray-500 italic">
                  {loadingBackups ? "Loading.." : "No backups found"}
                </td>
              </tr>
            ) : (
              backups.map((b, idx) => (
                <tr key={b.id} className="hover:bg-gray-300 even:bg-gray-200 odd:bg-white">
                  <td className="px-3 py-2 text-center">{idx + 1}</td>
                  <td className="px-3 py-2 text-center">{b.name}</td>
                  <td className="px-3 py-2 text-center">
                    {new Date(b.lastBackupTaken).toLocaleString("en-GB", {
                      weekday: "long",
                      day: "2-digit",
                      month: "short",
                      year: "numeric"
                    })} 3 AM
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
          <p className="italic text-gray-500">
            Backups are taken and kept for {retentionP} days. Remaining entries are hidden.
          </p>
      </div>
			{message && (
				<div className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none">
					<div className="bg-purple-600 text-white font-bold px-6 py-4 rounded-xl shadow-2xl text-lg animate-pulse opacity-95 pointer-events-auto">
						{message}
					</div>
				</div>
			)}

    </div>
  );
};

export default ChangesAndRecommendations;
